import type { ComponentType, ReactNode, SVGProps } from "react";
import {
  Calendar,
  CalendarX,
  Check,
  CheckCheck,
  CircleCheck,
  CircleHelp,
  Clock3,
  Files,
  Link2,
  Link2Off,
  ListChecks,
  Loader,
  MessageSquare,
  Package,
  PenLine,
  RotateCcw,
  Send,
  Sparkles,
  Upload,
  Users,
} from "lucide-react";
import { Button } from "@/components/form/Button";
import ElapsedSince from "../ElapsedSince";
import { cn } from "@/utils/helpers";
import type { BannerIcon, PostBanner } from "../postState";
import "./postPage.css";

type Icon = ComponentType<SVGProps<SVGSVGElement> & { strokeWidth?: number }>;

const ICONS: Record<BannerIcon, Icon> = {
  Sparkles,
  RotateCcw,
  PenLine,
  ListChecks,
  MessageSquare,
  Send,
  Clock: Clock3,
  Check,
  CheckCheck,
  CircleCheck,
  CalendarX,
  Upload,
  Loader,
  Package,
  CircleHelp,
  Files,
  Link2Off,
  Users,
};

export type PostViewport = "desktop" | "tablet" | "phone";

/** A quiet banner action: accent ink, no fill (the Figma's "Remind Fatafeat", "Approve for Fatafeat"). */
function QuietButton({ label, onPress, disabled }: { label: string; onPress: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onPress}
      className="shrink-0 rounded-pill px-3 py-1.5 text-[13.5px] font-medium text-co-accent-ink outline-none transition-opacity hover:opacity-80 focus-visible:outline-2 focus-visible:outline-co-accent disabled:opacity-50"
    >
      {label}
    </button>
  );
}

/** The one primary, as the page renders it. Send actions carry a trailing Send. */
function PrimaryButton({
  banner,
  busy,
  onPrimary,
  className,
}: {
  banner: PostBanner;
  busy?: boolean;
  onPrimary: () => void;
  className?: string;
}) {
  const primary = banner.primary;
  if (!primary) return null;
  if (primary.quiet && !className) return <QuietButton label={primary.label} onPress={onPrimary} disabled={primary.disabled || busy} />;
  const sends = primary.action === "send-concept" || primary.action === "send-brief" || primary.action === "send-kit";
  const approves = primary.action === "approve-concept" || primary.action === "approve-brief";
  const icon = sends
    ? Send
    : approves
      ? Check
      : primary.action === "add-files"
        ? Upload
        : primary.action === "copy-kit-link"
          ? Link2
          : primary.action === "move-post"
            ? Calendar
            : primary.action === "mark-posted"
              ? Check
              : primary.action === "schedule-all"
                ? Calendar
                : undefined;
  return (
    <Button
      size="small"
      icon={icon}
      iconPosition="right"
      iconClassName="size-3.5"
      loading={busy}
      isDisabled={primary.disabled || busy}
      onPress={onPrimary}
      className={cn("shrink-0", className)}
    >
      {primary.label}
    </Button>
  );
}

/**
 * Whose turn it is, in one banner under the post header (DESIGN.md "The status
 * banner"; library Status banner, style B: a tonal band at radius 16 with no
 * outline, pegs retired 1 Oct 2026): a tonal icon, the state as a title, one sentence of meta that
 * starts with the stage, and the page's one primary. The band scrolls with the
 * post (only the date rail island stays put, 14 Oct). On a phone the banner has
 * no button and the primary moves to a bottom bar.
 */
export default function StatusBanner({
  banner,
  viewport,
  busy,
  onPrimary,
  secondary,
  onSecondary,
  className,
  titleSince,
  metaSince,
}: {
  /** Runs the banner's own second action (`banner.secondary`), when it has one. */
  onSecondary?: () => void;
  /** Machine work in progress: a running "· 0:12 so far" after the title. */
  titleSince?: string | null;
  /** A clock that opens the meta line instead: "0:24 so far. …" (the brief being written). */
  metaSince?: string | null;
  banner: PostBanner;
  viewport: PostViewport;
  busy?: boolean;
  onPrimary: () => void;
  /** A plain action beside the primary (the client's Ask for changes). On a phone it sits under the meta. */
  secondary?: ReactNode;
  /** Spacing for the banner box. */
  className?: string;
}) {
  const IconGlyph = ICONS[banner.icon];
  const phone = viewport === "phone";

  const glyph = (
    <IconGlyph
      className={cn("size-[17px]", banner.icon === "Loader" && "motion-safe:animate-[spin_1.6s_linear_infinite]")}
      strokeWidth={1.8}
      aria-hidden="true"
    />
  );

  if (phone) {
    return (
      <>
        <div
          role="status"
          aria-live="polite"
          className={cn("relative flex min-h-[84px] items-center gap-4 rounded-panel bg-[var(--co-tonal)] px-5 py-4", className)}
        >
          <span className="grid size-9 shrink-0 place-items-center rounded-[4px] bg-co-tonal text-co-text">{glyph}</span>
          <div className="min-w-0 flex-1">
            <p className={cn("text-[16px] font-semibold leading-[22px]", banner.tone === "alert" ? "text-co-alert-ink" : "text-co-text")}>
              {banner.title}
              {titleSince ? <ElapsedSince since={titleSince} /> : null}
            </p>
            <p className="mt-1 line-clamp-2 text-[13.5px] leading-5 text-co-text2">
              {metaSince ? <ElapsedSince since={metaSince} prefix="" suffix=" so far. " /> : null}
              {banner.meta}
            </p>
            {secondary ? <div className="-ml-2 mt-1">{secondary}</div> : null}
          </div>
        </div>
        {banner.primary ? (
          <div className="co-post-glass co-post-bottombar fixed inset-x-0 bottom-0 z-30 border-t border-co-line">
            <PrimaryButton banner={banner} busy={busy} onPrimary={onPrimary} className="h-11 w-full text-[14px]" />
          </div>
        ) : null}
      </>
    );
  }

  const hasExtra = !!secondary || !!(banner.secondary && onSecondary);

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn("relative flex min-h-[84px] items-center gap-4 rounded-panel bg-[var(--co-tonal)] px-5 py-4", className)}
    >
      <span className="grid size-9 shrink-0 place-items-center rounded-[4px] bg-co-tonal text-co-text">{glyph}</span>
      <div className="min-w-0 flex-1">
        <p className={cn("flex items-baseline gap-1 text-[16px] font-semibold leading-[22px]", banner.tone === "alert" ? "text-co-alert-ink" : "text-co-text")}>
          <span className="min-w-0 truncate">{banner.title}</span>
          {titleSince ? (
            <span className="shrink-0">
              <ElapsedSince since={titleSince} />
            </span>
          ) : null}
        </p>
        <p className="mt-1 line-clamp-2 text-[13.5px] leading-5 text-co-text2">
          {metaSince ? <ElapsedSince since={metaSince} prefix="" suffix=" so far. " /> : null}
          {banner.meta}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {hasExtra ? (
          <div className="flex min-w-0 items-center gap-2">
            {secondary}
            {banner.secondary && onSecondary ? (
              <QuietButton label={banner.secondary.label} onPress={onSecondary} disabled={banner.secondary.disabled || busy} />
            ) : null}
          </div>
        ) : null}
        <PrimaryButton banner={banner} busy={busy} onPrimary={onPrimary} />
      </div>
    </div>
  );
}
